import { SITE } from './company';
import { ROUTES, type RoutePath } from './routes';

export interface PageSeo {
  /** Full <title>. Kept under ~60 characters so it survives the SERP. */
  title: string;
  /** Meta description, ~140-160 characters. */
  description: string;
  /** Absolute canonical URL. */
  canonical: string;
  /** og:type. */
  ogType: 'website' | 'article';
  /** schema.org type for the page-level WebPage node. */
  pageType: 'WebPage' | 'AboutPage' | 'ContactPage' | 'CollectionPage';
  /**
   * Escape hatch, currently unused by every route - and it should stay that
   * way. It once carried a comment claiming the legal pages were kept out of
   * the index, which was never true: nothing ever set it. Leaving them indexed
   * is the right call anyway. A payment provider or an enterprise buyer
   * checking whether this company publishes real terms needs to be able to
   * find them, and a `noindex` policy page is a policy page nobody can cite.
   */
  noindex?: boolean;
}

/*
 * Absolute canonical URL for a route, always with a trailing slash.
 *
 * The slash is not cosmetic: it is the URL that actually exists. Netlify answers
 * a request for `/services` with a 301 to `/services/`, so the slashless form
 * this used to emit named a redirect rather than a page - in the canonical tag,
 * in every sitemap <loc> and in the JSON-LD. Declaring "the canonical page is X"
 * where X redirects makes Search Console report every sitemap entry as "Page
 * with redirect", and it is what made getSeo below miss its own table.
 *
 * Route paths stay slashless (see routes.ts): they key this table and the
 * router, and only the published URL needs the slash.
 */
const abs = (path: string) => (path === '/' ? `${SITE.url}/` : `${SITE.url}${path}/`);

export const SEO: Record<RoutePath, PageSeo> = {
  '/': {
    title: 'Skabro - eCommerce Engineering & Custom Software',
    description:
      'A small Slovak engineering team building storefronts, platform applications, integrations and the systems behind them. Long-lived code, clear decisions, no theatre.',
    canonical: abs('/'),
    ogType: 'website',
    pageType: 'WebPage',
  },
  '/services': {
    title: 'Services - eCommerce Builds, Apps & Integrations | Skabro',
    description:
      'Storefronts and B2B portals, custom commerce logic, platform apps for Adobe Commerce, Shopify, Shopware and BigCommerce, ERP and PIM integrations, and solution architecture.',
    canonical: abs('/services'),
    ogType: 'website',
    pageType: 'CollectionPage',
  },
  '/company': {
    title: 'Company - Skabro s. r. o., Bratislava',
    description:
      'Skabro s. r. o. is a Slovak limited liability company building eCommerce and custom software for clients across Europe. Registration, VAT and contract details in full.',
    canonical: abs('/company'),
    ogType: 'website',
    pageType: 'AboutPage',
  },
  '/contact': {
    title: 'Contact Skabro - Tell Us What You Are Building',
    description:
      'A few lines about your project is enough to start. We reply personally, usually within one working day. Email hello@skabro.eu or use the enquiry form.',
    canonical: abs('/contact'),
    ogType: 'website',
    pageType: 'ContactPage',
  },
  '/payment-and-refunds': {
    title: 'Payment, Refund & Cancellation Policy | Skabro',
    description:
      'How Skabro s. r. o. quotes, invoices and is paid: individual quotations in EUR, delivery by milestone, 30 days notice to cancel, and how corrections and credit notes work.',
    canonical: abs('/payment-and-refunds'),
    ogType: 'article',
    pageType: 'WebPage',
  },
  '/privacy': {
    title: 'Privacy Policy | Skabro',
    description:
      'How Skabro s. r. o. processes personal data collected through skabro.eu, under GDPR (EU) 2016/679 and Slovak Act No. 18/2018 Coll.',
    canonical: abs('/privacy'),
    ogType: 'article',
    pageType: 'WebPage',
  },
  '/cookies': {
    title: 'Cookie Policy | Skabro',
    description:
      'skabro.eu sets no cookies of its own - no analytics, no advertising. The contact page loads Google reCAPTCHA, the single exception, explained here in full.',
    canonical: abs('/cookies'),
    ogType: 'article',
    pageType: 'WebPage',
  },
  '/terms': {
    title: 'Terms of Use | Skabro',
    description:
      'The terms governing use of skabro.eu, including site operator details, intellectual property, invoicing, liability and governing law.',
    canonical: abs('/terms'),
    ogType: 'article',
    pageType: 'WebPage',
  },
  '/accessibility': {
    title: 'Accessibility Statement | Skabro',
    description:
      'skabro.eu is built to conform with WCAG 2.2 level AA. How that was tested, the limitations we know about, and how to tell us about a barrier you hit.',
    canonical: abs('/accessibility'),
    ogType: 'article',
    pageType: 'WebPage',
  },
};

/**
 * The SEO record for a path, tolerating the trailing-slash form.
 *
 * This normalisation is load-bearing. Netlify serves `/services` as a 301 to
 * `/services/`, so the path a visitor and a crawler actually land on carries a
 * slash, while this table is keyed by the slashless route paths. Without the
 * trim the lookup missed on every subpage and fell back to the home record - and
 * because <Seo /> rewrites the head from whatever this returns after hydration,
 * each subpage overwrote its own title, description, og:url and canonical with
 * the home page's. Rendered with JavaScript, which is how Google sees the page,
 * all eight subpages declared themselves duplicates of `/` and were folded into
 * it. The prerendered HTML was correct; hydration undid it a moment later.
 *
 * Unknown paths still fall back to the home record, which is what the 404 page
 * and buildJsonLd rely on.
 */
export function getSeo(path: string): PageSeo {
  const route = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  return SEO[route as RoutePath] ?? SEO['/'];
}

/** Every canonical URL, for sitemap generation. */
export const SITEMAP_ENTRIES = ROUTES.map((route) => ({
  loc: abs(route.path),
  priority: route.priority,
  lastmod: route.updated,
}));
